// Library imports
import store, { RootState } from 'store';
import { useSelector } from 'react-redux';
import React, { Suspense, useEffect, useState } from 'react';

//  Local imports
import { TAB_EDITION } from 'constants/paths';
import useQuery from '../../../hooks/useQuery';
import { DESIGN } from 'constants/actionTypes';
import { TEMP_VERSION_NAME } from 'constants/index';
import { closeCurationSidebar } from 'modules/GlobalActions';
import ButtonSmall from 'modules/Common/Components/ButtonSmall';
import { showLoader } from 'modules/Design/Scripts/DynamicStripo';
import CreateAsset from '../CampaignAssets/Components/CreateAsset';
import { getDesignHTMLService, resetDesignPreview } from 'modules/Design/action';
import { updateEmailNameForBrowserEmailList } from 'modules/EmailBrowser/actions';
import { getAnalyticsFilterId, getQueryCampaignId, getQueryEmailId, getQueryWorkflowId } from 'modules/EmailBrowser/utils';
import { getDefaultTemplateStyleId, getQueryAccountId, getQueryContactId, getQueryParam } from 'utils';
import { getEditionArticles, resetEditionData, setEditionEmailId, updateEmailCampaignState } from './actions';

const EditionEdit = React.lazy(() => import('./EditionEditV2'));
const Design = React.lazy(() => import('modules/Design/Shared/Design'));
const EditionPageHeading = React.lazy(() => import('./EditionPageHeading'));
const Publish = React.lazy(() => import('modules/Publish/Components/Publish'));
const FavoritesTab = React.lazy(() => import('modules/Common/Components/FavoritesTab'));
const EditionSmartSourcedContent = React.lazy(() => import('./EditionSmartSourcedContent'));

interface Props {
  tab?: string;
}

const EditionEditWrapperV2 = (props: Props) => {
  // Props
  const { tab } = props;

  // Redux States
  const campaignId: number = useSelector((state: RootState) => state.editionData.assetId);
  const emailType: number = useSelector((state: RootState) => state.editionData.emailType);
  const workflowId: number = useSelector((state: RootState) => state.editionData.workflowId);
  const noCampaign: boolean = useSelector((state: RootState) => state.editionData.noCampaign);
  const previousEmailId: number = useSelector((state: RootState) => state.editionData.emailId);
  const editionName: string = useSelector((state: RootState) => state.editionData.editionName);
  const campaignName: string = useSelector((state: RootState) => state.editionData.digestName);
  const workflowName: string = useSelector((state: RootState) => state.editionData.workflowName);
  const curatedCount: number = useSelector((state: RootState) => state.editionData.curatedCount);
  const editionStatus: string = useSelector((state: RootState) => state.editionData.editionStatus);
  const adHocTemplatesloading: any = useSelector((state: RootState) => state.adHocTemplates.loading);
  const editionNameChangeLoading: boolean = useSelector((state: RootState) => state.editionData.nameChange.loading);
  const editionArticlesLoading: boolean = useSelector((state: RootState) => state.editionData.editionArticlesLoading);

  // Local States
  const [redirectUrl, setRedirectUrl] = useState('');
  const [articlesOrder, setArticlesOrder] = useState([]);
  const [firstTimeLoad, setFirstTimeLoad] = useState(true);
  const [publishLoader, setPublishLoader] = useState(false);
  const [previewLoader, setPreviewLoader] = useState(false);
  const [togglePreview, setTogglePreview] = useState(false);
  const [showDesignBtn, setShowDesignBtn] = useState(false);
  const [toggleDisabled, setToggleDisabled] = useState(true);
  const [prevArticlesOrder, setPrevArticlesOrder] = useState([]);
  const [redirectToPublish, setRedirectToPublish] = useState(false);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [isEmailWithoutCampaign, setIsEmailWithoutCampaign] = useState(false);

  // Local variables
  let query = useQuery();
  const isSource = tab !== 'source';
  const fsId = getAnalyticsFilterId();
  let queryEmailId = getQueryEmailId();
  let queryContactId = getQueryContactId();
  let queryAccountId = getQueryAccountId();
  let queryCampaignId = getQueryCampaignId(); // get campaignId from url
  const queryWorkflowId = getQueryWorkflowId();
  const generationId = getQueryParam('generation_id');
  // let goBackUrl =
  //   query.get('from') === 'workflow'
  //     ? `/automation/details?workflow_id=${workflowId ? workflowId : workflowIdFromUrl}`
  //     : workflowIdFromUrl
  //       ? `/automation/details?workflow_id=${workflowIdFromUrl}`
  //       : queryCampaignId && query.get('from') !== 'emailEngagement'
  //         ? `/campaigns/content?campaign_id=${queryCampaignId}`
  //         : query.get('from') === 'emailEngagement'
  //           ? queryContactId
  //             ? `/analytics/contacts/email_engagement/?contact_id=${queryContactId}&row_id=0&${fsId ? `fs_id=${fsId}` : ''}`
  //             : `/analytics/accounts/email_engagement/?account_id=${queryAccountId}&row_id=0&${fsId ? `?fs_id=${fsId}` : ''}`
  //           : '/campaigns';

  const gobackUrl = () => {
    let url = ''
    if (query.get('from') === 'workflow') {
      url = `/automation/details?workflow_id=${workflowId ? workflowId : queryWorkflowId}`
    }
    else if (query.get('from') === 'emailEngagement') {
      if (queryContactId) {
        url = `/analytics/contacts/email_engagement/?contact_id=${queryContactId}${fsId ? `&fs_id=${fsId}` : ''}`
      } else {
        url = `/analytics/accounts/email_engagement/?account_id=${queryAccountId}${fsId ? `&fs_id=${fsId}` : ''}`
      }
      if (queryWorkflowId) {
        url = url + `&workflow_id=${queryWorkflowId}`
      }
    } else if (queryCampaignId) {
      url = `/campaigns/content?campaign_id=${queryCampaignId}`
    }
    else {
      url = '/campaigns';
    }
    return url
  }
  // show preview for email design
  const showPreview = (html_content: any, email_content: string, css_content: any) => {
    if (email_content === undefined && html_content?.html) {
      store.dispatch({
        type: DESIGN.UPDATE_HTML,
        email_html: html_content?.email_html,
        html: html_content?.html,
        css: css_content?.css,
      });
    } else {
      store.dispatch({
        type: DESIGN.UPDATE_HTML,
        email_html: email_content,
        html: html_content,
        css: css_content,
      });
    }
    store.dispatch({
      type: DESIGN.LOAD,
      loader: false,
    });
    showLoader(false);
    if (!firstTimeLoad) setTogglePreview(true);
  };

  // first render useEffect
  useEffect(() => {
    //! need to check if this can be implemented with previous email id
    // resetting design data for the very first time and then getting it back on the first load
    resetDesignPreview();
    if (!window.location.pathname.includes('publish') && !window.location.pathname.includes('design') && queryCampaignId)
      getDesignHTMLService(getQueryEmailId(), getDefaultTemplateStyleId(), 0, showPreview, false);
    closeCurationSidebar();
    if (previousEmailId?.toString() !== queryEmailId?.toString() && !editionArticlesLoading && !adHocTemplatesloading) {
      resetEditionData();
      getEditionArticles(queryEmailId);
    }
    setFirstTimeLoad(false);
  }, []);

  // if campaign id is present in url, then set isEmailWithoutCampaign to false
  useEffect(() => {
    if (queryCampaignId && isEmailWithoutCampaign) {
      setIsEmailWithoutCampaign(false);
      updateEmailCampaignState();
    }
    if (noCampaign && !isEmailWithoutCampaign) {
      setIsEmailWithoutCampaign(true);
    }
  }, [queryCampaignId, isEmailWithoutCampaign, noCampaign]);

  useEffect(() => {
    if (queryEmailId.toString() !== previousEmailId.toString()) {
      setEditionEmailId(queryEmailId);
    }
  }, [queryEmailId, previousEmailId]);

  // functions
  const nameChangeHandler = (name: string) => {
    generationId ? updateEmailNameForBrowserEmailList(name, null, generationId) : updateEmailNameForBrowserEmailList(name, queryEmailId);
  };

  // save to campaign in case of Email without campaign
  const savetoCampaign = (value: boolean, url: string) => {
    setShowCreateCampaign(value);
    setRedirectUrl(url);
  };

  // edit design button click handler for email without campaig
  const EditDesignAction = (
    <div className="d-inline-flex">
      <CreateAsset
        noDropdown={true}
        noPortal={true}
        updateAsset={true}
        assetId={queryEmailId?.toString()}
        name={editionName}
        noRedirection={false}
        redirectURL={redirectUrl}
        callbackMethod={setIsEmailWithoutCampaign}
        className="hide-mac-article"
      >
        <span className="button-small-spacing">
          <ButtonSmall
            className={tab === 'design' ? 'styling-tag-green' : 'styling-tag-grey'}
            secondary
            value="Edit Design"
            onClick={() => {
              store.dispatch({
                type: DESIGN.RESET,
              });
              savetoCampaign(true, '/campaigns/editionedit/design/' + window.location.search);
            }}
          />
        </span>
      </CreateAsset>
    </div>
  );

  // showing subscription message
  const subHeading: { [key: string]: string } = {
    '0': 'Digest',
    '1': 'Ad hoc',
    '2': 'AI Email',
    '-1': '',
  };

  // main heading/name/subject of the email
  const mainHeading: string = editionArticlesLoading
    ? ''
    : !editionName.includes(TEMP_VERSION_NAME)
      ? editionName
      : editionStatus === 'Sent'
        ? '[Sent] ' + { editionName }
        : 'Edition Draft';

  return (
    <>
      <Suspense fallback={<></>}>
        <EditionPageHeading
          toggleDisabled={toggleDisabled}
          setToggleDisabled={setToggleDisabled}
          publishLoader={publishLoader}
          setPublishLoader={setPublishLoader}
          setRedirectToPublish={setRedirectToPublish}
          previewLoader={previewLoader}
          setPreviewLoader={setPreviewLoader}
          togglePreview={togglePreview}
          setTogglePreview={setTogglePreview}
          showPreview={showPreview}
          headingSmallGreen={subHeading[emailType?.toString()]}
          emailType={emailType}
          headingMain={mainHeading}
          editionArticlesLoading={editionArticlesLoading}
          nameChangeHandler={nameChangeHandler}
          itemNameLoading={editionNameChangeLoading}
          curatedCount={curatedCount}
          showDesignBtn={showDesignBtn}
          editionStatus={editionStatus}
          assetId={campaignId}
          campaignId={campaignId}
          campaignName={campaignName}
          workflowName={workflowName}
          tab={tab}
          page={'editions'}
          isEmailWithoutCampaign={isEmailWithoutCampaign}
          overlayClass={'asset-header header-truncate-remove'}
          goBackURL={gobackUrl()}
          editDesignAction={EditDesignAction}
          enableStickyHeader={true}
          prevArticlesOrder={prevArticlesOrder}
          articlesOrder={articlesOrder}
          setIsEmailWithoutCampaign={setIsEmailWithoutCampaign}
        />
      </Suspense>
      {tab === TAB_EDITION.SOURCE ? (
        <Suspense fallback={<></>}>
          <EditionSmartSourcedContent isAssetWithoutCampaign={isEmailWithoutCampaign} />
        </Suspense>
      ) : tab === TAB_EDITION.FAVORITES ? (
        <Suspense fallback={<></>}>
          <FavoritesTab isEdition />
        </Suspense>
      ) : tab === TAB_EDITION.PUBLISH ? (
        <Suspense fallback={<></>}>
          <Publish />
        </Suspense>
      ) : tab === TAB_EDITION.DESIGN ? (
        <Suspense fallback={<></>}>
          <Design
            redirectToPublish={redirectToPublish}
            setRedirectToPublish={setRedirectToPublish}
            toggleDisabled={toggleDisabled}
            setToggleDisabled={setToggleDisabled}
            setPublishLoader={setPublishLoader}
          />
        </Suspense>
      ) : (
        <Suspense fallback={<></>}>
          <EditionEdit
            setShowDesignBtn={setShowDesignBtn}
            setIsEmailWithoutCampaign={setIsEmailWithoutCampaign}
            isEmailWithoutCampaign={isEmailWithoutCampaign}
            savetoCampaign={savetoCampaign}
            redirectUrl={redirectUrl}
            showCreateCampaign={showCreateCampaign}
            setPrevArticlesOrder={setPrevArticlesOrder}
            setArticlesOrder={setArticlesOrder}
            previewLoader={previewLoader}
            setPreviewLoader={setPreviewLoader}
            isSource={isSource}
          />
        </Suspense>
      )}
    </>
  );
};

export default EditionEditWrapperV2;
